import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import DocumentsSignHeader from 'components/views/DocumentsSign/DocumentsSignHeader'
import SetPayDay from 'components/views/DocumentsSign/SetPayDay'
import FlowStepsList, { FlowStepType } from 'components/views/FlowSteps'
import { useAppSelector } from 'hooks'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getCreditParamsSelector, getCurrentFlowType, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import tw from 'twin.macro'
import { getWsStepStatuses } from 'utils'
import Emitter from 'utils/emitter'
import { v4 as uuidv4 } from 'uuid'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const dummyFunction = () => {}
const dummyFunction2 = () => false

const DamuSecondConfirmationPage = () => {
  // Tools
  const { t } = useTranslation()

  // Selectors
  const currentFlowType = useAppSelector(getCurrentFlowType)
  const { current_step } = useAppSelector(getStatusSelector)
  const { pay_day } = useAppSelector(getCreditParamsSelector)
  const {
    REGISTER_IN_COLVIR_DAMU_PROGRAM,
    GENERATE_REPAYMENT_SCHEDULE,
    CREATE_MORTGAGE,
    SEND_ADDITIONAL_DOCUMENTS_TO_CRED_ADMIN,
    SECOND_CONFIRMATION_FROM_CRED_ADMIN
  } = useAppSelector(getNotificationsSelector)

  // Computed | memoized values
  const colvirStepStatus = useMemo<FlowStepType>(() => {
    if (!currentFlowType)
      return {
        id: uuidv4(),
        status: 'STEP_NOT_STARTED',
        apiSteps: [],
        title: t('flow-deal-register'),
        subtitle: t(t('flow-deal-register2'))
      }
    return {
      id: uuidv4(),
      status: getWsStepStatuses(REGISTER_IN_COLVIR_DAMU_PROGRAM),
      apiSteps: [FLOW_STEPS.REGISTER_IN_COLVIR_DAMU_PROGRAM],
      title: t('flow-deal-register'),
      subtitle: t('flow-deal-register2')
    }
  }, [currentFlowType, t, REGISTER_IN_COLVIR_DAMU_PROGRAM])

  const documentsStepStatus = useMemo<FlowStepType>(() => {
    if (!currentFlowType)
      return {
        id: uuidv4(),
        status: 'STEP_NOT_STARTED',
        apiSteps: [],
        title: t('flow-generate-doc-pack'),
        subtitle: t('flow-generate-doc-pack2')
      }

    return {
      id: uuidv4(),
      status: getWsStepStatuses(GENERATE_REPAYMENT_SCHEDULE, CREATE_MORTGAGE, SEND_ADDITIONAL_DOCUMENTS_TO_CRED_ADMIN),
      apiSteps: [
        FLOW_STEPS.GENERATE_REPAYMENT_SCHEDULE,
        FLOW_STEPS.CREATE_MORTGAGE,
        FLOW_STEPS.SEND_ADDITIONAL_DOCUMENTS_TO_CRED_ADMIN
      ],
      title: t('flow-generate-doc-pack'),
      subtitle: t('flow-generate-doc-pack2')
    }
  }, [CREATE_MORTGAGE, GENERATE_REPAYMENT_SCHEDULE, SEND_ADDITIONAL_DOCUMENTS_TO_CRED_ADMIN, currentFlowType, t])

  const credAdminStepStatus = useMemo<FlowStepType>(() => {
    if (!currentFlowType)
      return {
        status: 'STEP_NOT_STARTED',
        apiSteps: [],
        id: uuidv4(),
        title: t('flow-check-dossier'),
        subtitle: t('flow-sms-after')
      }

    return {
      id: uuidv4(),
      status: getWsStepStatuses(SECOND_CONFIRMATION_FROM_CRED_ADMIN),
      apiSteps: [FLOW_STEPS.SECOND_CONFIRMATION_FROM_CRED_ADMIN],
      title: t('flow-check-dossier'),
      subtitle: t('flow-sms-after')
    }
  }, [SECOND_CONFIRMATION_FROM_CRED_ADMIN, currentFlowType, t])

  const stepsArray = useMemo(() => {
    return [colvirStepStatus, documentsStepStatus, credAdminStepStatus]
  }, [colvirStepStatus, documentsStepStatus, credAdminStepStatus])

  const currStepIdx = useMemo(
    () =>
      stepsArray.findLastIndex(step => step.apiSteps.includes(current_step?.step as FLOW_STEPS)) ||
      stepsArray.findLastIndex(step => step.status === 'IN_PROGRESS' || step.status === 'DONE'),
    [current_step?.step, stepsArray]
  )

  // Effects
  useEffect(() => {
    const currentFlowStep = stepsArray.at(currStepIdx)
    if (!currentFlowStep) return
    if (currentFlowStep.status !== 'DONE') return

    Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
  }, [currStepIdx, stepsArray])

  return (
    <ContainerWithSteps>
      <div>
        <DocumentsSignHeader />
        <SetPayDay
          payDay={pay_day}
          repaymentDay={0}
          isLoading={false}
          onRepaymentDayChange={dummyFunction}
          disabledPickerDays={dummyFunction2}
          onSetPayDay={dummyFunction}
        />
        <FlowStepsList
          twStyle={tw`mt-5`}
          steps={stepsArray}
          isDoneCondition={(_, index) => currStepIdx > index}
          isInProgressCondition={step => step.apiSteps.includes(current_step?.step as FLOW_STEPS)}
        />
      </div>
    </ContainerWithSteps>
  )
}

export default DamuSecondConfirmationPage
